import React, { useContext, useState } from 'react';
import WaiverTextField from './editor/WaiverTextField';
import YesOrNoSelector from './editor/YesOrNoSelector';
import WaiverLinks from './editor/WaiverLinks';
import WaiverSingleItemList from './editor/WaiverSingleItemList';
import { WaiverContext } from './WaiverEditConfigView';

const WaiverConfigEditor: React.FC = () => {
  const {
    waiver,
    setWaiver,
    templateAirlineCode,
    authorizedAirlineCodes,
    canEditAllConfigFields,
    isWaiverIssuedByAmerican,
    waiverConfig,
    setWaiverConfig,
    changeIssuingAirline,
    submit,
    cancelChanges,
    formHasNoChanges
  } = useContext(WaiverContext);

  const handleInputChange = (field: string, value: any) => {
    setWaiver({ ...waiver, [field]: value });
  };

  const handleConfigChange = (field: string, value: any) => {
    setWaiverConfig({ ...waiverConfig, [field]: value });
  };

  return (
    <div className="row waiver-editor">
      <div className="row waiver-title">
        <div className="col-md-12">
          {templateAirlineCode} Configuration
        </div>
      </div>
      <form className="waiver-form form-inline" name="waiverform" role="form">
        <div className="col-md-12 waiver-section" id="waiver-header">
          <div className="col-md-12 no-padding waiver-section-header">
            <div className="col-md-6" id="waiver-header-left">
              <div className="form-group">
                <label htmlFor="waiverIssuedAirline">Issuing Airline*:</label>
                <select className="form-control dropdown-toggle issuing-airline" id="waiverIssuedAirline"
                  name="waiver-issued-airline" value={waiver.issuingAirlineCode}
                  onChange={(e) => {
                    handleInputChange('issuingAirlineCode', e.target.value);
                    changeIssuingAirline(e.target.value);
                  }} required>
                  {authorizedAirlineCodes.map((airlineCode: any) => (
                    <option key={airlineCode} value={airlineCode}>{airlineCode}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-6" id="waiver-header-right"></div>
          </div>
          <div className="col-md-12 no-padding" id="waiver-body">
            <div className="col-md-6" id="waiver-body-left">
              {canEditAllConfigFields() && (
                <>
                  <div className="form-group">
                    <label>Entered by airline:</label>
                    <div className="input-container">
                      <div className="btn-group">
                        <button type='button'
                          className={`btn btn-primary ${waiverConfig.enteredByAirline === true ? 'active' : ''}`}
                          onClick={() => handleConfigChange('enteredByAirline', true)}
                        >
                          Yes
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiverConfig.enteredByAirline === false ? 'active' : ''}`}
                          onClick={() => handleConfigChange('enteredByAirline', false)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Non-urgent airline:</label>
                    <div className="input-container">
                      <div className="btn-group">
                        <button type='button'
                          className={`btn btn-primary ${waiverConfig.nonUrgent === true ? 'active' : ''}`}
                          onClick={() => handleConfigChange('nonUrgent', true)}
                        >
                          Yes
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiverConfig.nonUrgent === false ? 'active' : ''}`}
                          onClick={() => handleConfigChange('nonUrgent', false)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <WaiverTextField label="Ticket Stock(s)" placeholder="Example: 005, 016" value={waiver.ticketStocks}
                onChange={(value: string) => handleInputChange('ticketStocks', value)} errorTooltip={false}/>
              <WaiverTextField large label="Ticket Stock Instructions" value={waiverConfig.ticketStockInstructions}
                onChange={(value: string) => handleConfigChange('ticketStockInstructions', value)} errorTooltip={false}/>
              <WaiverTextField large label="New Travel Dates Details" value={waiver.newTravelDates}
                onChange={(value: string) => handleInputChange('newTravelDates', value)} errorTooltip={false}/>
              <YesOrNoSelector label="Refunds Applicable" value={waiver.refundsApplicable}
                onChange={(value: boolean) => handleInputChange('refundsApplicable', value)} />
              <WaiverLinks label="Policy URLs" addButtonLabel="Add Policy Link" links={waiver.policyLinks}
                onChange={(value: any) => handleInputChange('policyLinks', value)} />
            </div>
            <div className="col-md-6" id="waiver-body-right">
              <WaiverTextField label="Waiver Code" placeholder="Example: 1A2B3C4D"
                // condition={isWaiverIssuedByAmerican()} 
                value={waiver.code} onChange={(value: string) => handleInputChange('code', value)} errorTooltip={false}/>
              {canEditAllConfigFields() && (
                <>
                  <WaiverTextField label="Waiver Filter Keywords" value={waiverConfig.waiverKeywords}
                    onChange={(value: string) => handleConfigChange('waiverKeywords', value)} errorTooltip={false}/>
                </>
              )}
              <WaiverTextField large label="OSI Requirements" value={waiver.osiRequirements}
                onChange={(value: string) => handleInputChange('osiRequirements', value)} errorTooltip={false}/>
              <WaiverTextField large label="Additional Info" value={waiver.additionalInfo}
                onChange={(value: string) => handleInputChange('additionalInfo', value)} errorTooltip={false}/>
              <WaiverTextField large label="Waiver Entry Instructions" value={waiverConfig.entryInstructions}
                onChange={(value: string) => handleConfigChange('entryInstructions', value)} errorTooltip={false}/>
              {canEditAllConfigFields() && (
                <>
                  <WaiverLinks label="Airline Info Links" addButtonLabel="Add Airline Link" links={waiverConfig.airlineInfoLinks}
                    onChange={(value: object) => handleConfigChange('airlineInfoLinks', value)} />
                  <WaiverSingleItemList label="Airline Email Domains" addButtonLabel="Add Email Domain" fieldLabel="Email Domain"
                    items={waiverConfig.acceptableEmailDomains} onChange={(value: string[]) => handleConfigChange('acceptableEmailDomains', value)} />
                </>
              )}
            </div>
          </div>
          <div className="col-md-12">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <hr className="waiver-half-section-separator" />
              <div className="form-buttons">
                <button className="btn btn-default waiver-button" type="button" id="cancelWaiverButton" onClick={cancelChanges}>Cancel Changes</button>
                <button className="btn btn-default waiver-button margin-left-5" type="button" id="saveWaiverButton"
                  // disabled={formHasNoChanges()}
                onClick={(e) => { e.preventDefault(); submit(waiver); }}>Save Config</button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WaiverConfigEditor;